
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------
const CheckIcon = () => (<svg className='h-4 w-4 text-teal-400 flex-shrink-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
  </svg>);
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='inline-block text-xs font-bold tracking-[0.2em] uppercase mb-4' style={{ color: '#5eead4' }}>
    {children}
  </span>);
// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SeedanceLandingPage() {
    const { t } = useLocale('seedance-2');
    const creationModes = [
        {
            mode: t('Text to Video'),
            tag: 'T2V',
            description: t('Describe any scene in natural language and Seedance 2.0 brings it to life with synchronized dialogue, foley, and ambient audio — all generated natively.'),
            details: [
                t('Detailed scene & action control via prompts'),
                t('Native audio generation (dialogue + SFX + ambience)'),
                t('All 6 aspect ratios supported'),
                t('4–12 second output duration'),
            ],
        },
        {
            mode: t('Image to Video'),
            tag: 'I2V',
            description: t('Upload a still image and watch it animate with realistic motion while preserving every detail — facial features, clothing, background, and lighting.'),
            details: [
                t('Identity-preserving animation'),
                t('Use up to 9 reference images for characters & scenes'),
                t('Automatic camera movement generation'),
                t('Consistent style across frames'),
            ],
        },
        {
            mode: t('Video Reference'),
            tag: 'V2V',
            description: t('Upload reference videos to extract camera movements, character motion, and timing. Seedance 2.0 intelligently reproduces cinematic techniques without complex prompting.'),
            details: [
                t('Camera trajectory extraction & reproduction'),
                t('Facial expression transfer from reference footage'),
                t('Motion duplication with new characters/scenes'),
                t('Up to 3 reference videos (15s total)'),
            ],
        },
        {
            mode: t('Audio-Driven Video'),
            tag: 'A2V',
            description: t('Use voiceovers, soundtracks, or narration as the primary control signal. The model generates visuals that match the rhythm, emotion, and timing of your audio.'),
            details: [
                t('Beat-matched visual generation'),
                t('Lip-sync from uploaded voiceover'),
                t('Music-driven scene transitions'),
                t('Up to 3 audio files (15s total)'),
            ],
        },
    ];
    const cameraFeatures = [
        {
            name: t('Hitchcock Zoom'),
            description: t('The classic dolly-zoom effect that creates a disorienting perspective shift — perfect for dramatic reveals and protagonist panic moments.'),
        },
        {
            name: t('Tracking Shots'),
            description: t('Follow subjects through environments with smooth rear, side, and frontal tracking. Low-angle and high-angle variants supported.'),
        },
        {
            name: t('Circling / Orbit'),
            description: t('Multi-angle orbiting around subjects with natural acceleration and deceleration. Create dynamic 180° and 360° reveals.'),
        },
        {
            name: t('Crane & Boom'),
            description: t('Vertical camera movements that sweep up from ground level or descend from aerial perspectives with fluid motion.'),
        },
        {
            name: t('Pan & Tilt'),
            description: t('Precise degree-based panning (90°, 180°) with halt-and-resume control. Follow subject gaze direction naturally.'),
        },
        {
            name: t('Push / Pull Zoom'),
            description: t('Smooth zoom into close-ups or pull back to reveal wider scenes. Control pacing of the zoom for dramatic or subtle effect.'),
        },
    ];
    const visualStyles = [
        { name: t('Photorealistic'), desc: t('True-to-life quality') },
        { name: t('Cinematic Film'), desc: t('Hollywood color grading') },
        { name: t('Anime'), desc: t('Japanese animation style') },
        { name: t('3D Animation'), desc: t('Pixar-quality rendering') },
        { name: t('2D Animation'), desc: t('Flat illustration style') },
        { name: t('Watercolor'), desc: t('Painterly soft edges') },
        { name: t('Film Noir'), desc: t('High-contrast B&W drama') },
        { name: t('Abstract'), desc: t('Non-representational art') },
    ];
    const specs = [
        {
            label: t('Max Resolution'),
            value: '2K',
            sub: '1080p standard',
        },
        { label: t('Duration'), value: '4–12s', sub: t('Per generation') },
        {
            label: t('Aspect Ratios'),
            value: '6',
            sub: '16:9 · 9:16 · 4:3 · 3:4 · 21:9 · 1:1',
        },
        {
            label: t('Input Modalities'),
            value: '4',
            sub: t('Text · Image · Video · Audio'),
        },
        {
            label: t('Max Input Files'),
            value: '12',
            sub: t('Combined across modalities'),
        },
        {
            label: t('Reference Images'),
            value: '9',
            sub: t('Characters, objects, scenes'),
        },
        {
            label: t('Reference Videos'),
            value: '3',
            sub: t('15 seconds total max'),
        },
        {
            label: t('Audio Files'),
            value: '3',
            sub: t('15 seconds total max'),
        },
        {
            label: t('Lip-Sync Languages'),
            value: '8+',
            sub: t('EN · ZH · KO · JA · ES · ID & more'),
        },
        {
            label: t('Audio Layers'),
            value: '3',
            sub: t('Dialogue · Foley · Ambience'),
        },
        {
            label: t('Speed vs v1.5'),
            value: '+30%',
            sub: t('Faster generation'),
        },
        {
            label: t('Visual Styles'),
            value: '8+',
            sub: t('Photorealistic to abstract'),
        },
    ];
    const comparisonItems = [
        {
            feature: t('Resolution'),
            v2: t('Up to 2K'),
            v15: t('1080p'),
        },
        {
            feature: t('Native Audio'),
            v2: t('Dialogue + Foley + Ambience'),
            v15: t('Basic audio sync'),
        },
        {
            feature: t('Lip-Sync'),
            v2: t('8+ languages, phoneme-level'),
            v15: t('Limited language support'),
        },
        {
            feature: t('Input Modalities'),
            v2: t('Text + Image + Video + Audio'),
            v15: t('Text + Image only'),
        },
        {
            feature: t('Max Input Files'),
            v2: t('12 files simultaneously'),
            v15: t('1–2 files'),
        },
        {
            feature: t('Character Consistency'),
            v2: t('Extreme — multi-shot IP continuity'),
            v15: t('Good — single shot'),
        },
        {
            feature: t('Multi-Shot Storytelling'),
            v2: t('Yes — auto scene transitions'),
            v15: t('Not supported'),
        },
        {
            feature: t('Camera Control'),
            v2: t('Reference video extraction'),
            v15: t('Prompt-only'),
        },
        {
            feature: t('Video Editing'),
            v2: t('Natural language editing'),
            v15: t('Not supported'),
        },
        {
            feature: t('Generation Speed'),
            v2: t('30% faster'),
            v15: t('Baseline'),
        },
    ];
    const howItWorks = [
        {
            step: '01',
            title: t('Choose Your Inputs'),
            description: t('Start with a text prompt, upload reference images for characters and scenes, add reference videos for camera movements, or provide audio for lip-sync and beat matching.'),
        },
        {
            step: '02',
            title: t('Configure Output'),
            description: t('Select your aspect ratio (16:9, 9:16, 1:1, etc.), video duration (4–12 seconds), and desired visual style — from photorealistic to anime to film noir.'),
        },
        {
            step: '03',
            title: t('Reference with @Tags'),
            description: t('In your prompt, use @Image1, @Image2, @Video1 notation to tell the model exactly how each reference file should influence the output.'),
        },
        {
            step: '04',
            title: t('Generate & Iterate'),
            description: t('Seedance 2.0 processes your multimodal inputs and generates video with synchronized audio. Refine your prompt or swap references to iterate.'),
        },
    ];
    const promptTips = [
        {
            title: t('Use Camera Language'),
            description: t('Include specific cinematography terms: "Hitchcock zoom", "tracking shot following the subject", "slow 180° pan", "low-angle crane rising". Seedance 2.0 understands professional film vocabulary.'),
            example: t('A man in a dark suit walks through a neon-lit corridor. Camera starts with a rear tracking shot, then transitions to a circling orbit as he reaches the elevator.'),
        },
        {
            title: t('Reference Your Uploads'),
            description: t('Use @Image and @Video notation in prompts to bind specific files to roles. Assign images to characters, locations, or style references, and videos to camera or motion references.'),
            example: t('@Image1 is the main character. @Image2 is the office location. @Video1 provides the camera movement. The character sits at a desk, picks up a phone, and looks out the window.'),
        },
        {
            title: t('Describe Emotion & Motion'),
            description: t('Go beyond visual descriptions. Include emotional states, breathing patterns, micro-expressions, and body language for more realistic character animation.'),
            example: t('The woman looks up from her book, eyes widening with surprise. She stands slowly, her hands trembling slightly, and takes a hesitant step forward.'),
        },
        {
            title: t('Layer Audio Direction'),
            description: t('Describe the soundscape you want: dialogue content, ambient sounds, and action-specific foley. The model generates all three audio layers natively.'),
            example: t('A busy cafe scene. Background chatter and clinking cups. A barista calls out an order. Rain patters against the window. The protagonist sighs and stirs their coffee.'),
        },
    ];
    const useCases = [
        {
            title: t('Social Media Content'),
            description: t('Create scroll-stopping videos for TikTok, Instagram Reels, and YouTube Shorts. Native audio generation means your content is ready to post — no audio editing needed.'),
            icon: '📱',
        },
        {
            title: t('Product Marketing'),
            description: t('Generate cinematic product reveals with professional camera movements. Upload a product photo, add a reference video for the camera technique, and get a polished commercial.'),
            icon: '🛍️',
        },
        {
            title: t('Short Films & Narratives'),
            description: t('Produce multi-shot story sequences with consistent characters across scenes. Automatic transitions and character identity persistence enable episodic content creation.'),
            icon: '🎞️',
        },
        {
            title: t('Educational Content'),
            description: t('Create engaging explainers with synchronized voiceover lip-sync in 8+ languages. Upload narration audio and let the model generate matching visuals.'),
            icon: '📚',
        },
        {
            title: t('Music Videos'),
            description: t('Generate beat-matched visuals from audio input. The model synchronizes scene transitions, character movement, and camera cuts to the rhythm of your music.'),
            icon: '🎶',
        },
        {
            title: t('Brand Storytelling'),
            description: t('Build episodic content series with extreme character consistency. Your brand mascot, spokesperson, or product maintains identity across every shot.'),
            icon: '💎',
        },
        {
            title: t('Film Pre-visualization'),
            description: t('Use reference videos to prototype complex camera movements before actual production. Test Hitchcock zooms, crane shots, and orbit sequences virtually.'),
            icon: '🎯',
        },
        {
            title: t('Multilingual Content'),
            description: t('Generate the same video with lip-sync in English, Mandarin, Korean, Japanese, Spanish, Indonesian, and more — from a single prompt with different audio.'),
            icon: '🌍',
        },
    ];
    const faqs = [
        {
            question: t('What is Seedance 2.0?'),
            answer: t("Seedance 2.0 is ByteDance's next-generation AI video model. It generates video with native audio (dialogue, foley, and ambience) from four input types: text, images, video references, and audio. It outputs up to 2K resolution with extreme character consistency and professional camera techniques."),
        },
        {
            question: t('What makes Seedance 2.0 different from other AI video models?'),
            answer: t("Three key differentiators: (1) Native audio-visual generation — audio isn't post-processed but generated jointly with video, enabling true lip-sync and beat matching. (2) Reference video input — upload existing videos to extract and reproduce camera movements and character motion without complex prompting. (3) Multi-shot storytelling with extreme character consistency across scenes."),
        },
        {
            question: t('How does the multimodal input system work?'),
            answer: t('You can combine up to 12 files: up to 9 images (for characters, locations, style references), up to 3 video clips (for camera movements and motion references, 15s total), and up to 3 audio files (for voiceover and music, 15s total). Use @Image1, @Video1 notation in your prompt to assign roles to each file.'),
        },
        {
            question: t('What camera techniques does Seedance 2.0 support?'),
            answer: t('Seedance 2.0 understands professional cinematography language including: Hitchcock zoom (dolly-zoom), tracking shots (rear, side, frontal), orbiting/circling shots, crane and boom movements, pan and tilt with precise degree control, push/pull zoom, and robotic arm multi-angle effects. You can describe these in text or upload a reference video.'),
        },
        {
            question: t('What resolutions and formats are supported?'),
            answer: t('Output: up to 2K resolution (1080p standard), 4–12 second duration, 6 aspect ratios (16:9, 9:16, 4:3, 3:4, 21:9, 1:1). The model supports photorealistic, anime, 2D/3D animation, watercolor, film noir, and abstract visual styles.'),
        },
        {
            question: t('How does the native audio generation work?'),
            answer: t('Seedance 2.0 generates three audio layers simultaneously with the video: Dialogue (with phoneme-level lip-sync in 8+ languages), Foley (action-matched sound effects like footsteps, impacts, and environmental interactions), and Ambience (background audio like wind, crowds, rain). All layers are synchronized with the visual content.'),
        },
        {
            question: t('Can I control character appearance across multiple shots?'),
            answer: t('Yes. Seedance 2.0 features extreme character consistency — facial features, clothing details, accessories, and visual style are maintained uniformly across multi-shot narratives. Upload character reference images and the model maintains identity persistence throughout the generated sequence.'),
        },
        {
            question: t('How does video reference input work?'),
            answer: t('Upload up to 3 reference videos (15s total). The model extracts camera trajectories, character motion patterns, and facial expressions from the reference footage, then applies them to your generated video with new characters and scenes. This replaces complex text-based camera direction.'),
        },
        {
            question: t('What languages are supported for lip-sync?'),
            answer: t('Seedance 2.0 supports phoneme-level lip-sync in 8+ languages: English, Mandarin Chinese, Cantonese, Korean, Japanese, Spanish, Indonesian, and more. You can generate the same scene with lip-sync in different languages by changing the audio input.'),
        },
        {
            question: t('How many credits does Seedance 2.0 cost?'),
            answer: t('Seedance 2.0 uses per-second pricing. Credit costs scale with duration — shorter clips cost less, longer clips cost more. Visit the video generation page for current credit rates.'),
        },
        {
            question: t('Can I use Seedance 2.0 videos commercially?'),
            answer: t('Yes. Videos generated with Seedance 2.0 on Easy-Peasy.AI can be used for commercial purposes including marketing, advertising, social media, product videos, and content creation, subject to our terms of service.'),
        },
        {
            question: t('How fast is Seedance 2.0 compared to previous versions?'),
            answer: t('Seedance 2.0 is 30% faster than Seedance 1.5 Pro while delivering higher resolution (up to 2K vs 1080p), more input modalities, and native multi-layer audio generation. Typical generation completes in under 60 seconds for standard clips.'),
        },
    ];
    const videoExamples = [
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo7-6-4m.mp4',
            label: t('One-Take Tracking Shot'),
            description: t('Continuous camera following subjects through multiple urban locations'),
        },
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo5-2-4m.mp4',
            label: t('Cinematic with Native Audio'),
            description: t('Sunset cinematography with vintage car, foley and ambient sound'),
        },
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo3-5-4m.mp4',
            label: t('Action Scene Compositing'),
            description: t('Fight scene under starry sky with dust effects and dynamic camera'),
        },
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo8-6-4m.mp4',
            label: t('Emotional Narrative'),
            description: t('Story generated from image and audio references with character expression'),
        },
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo6-3-4m.mp4',
            label: t('Character Replacement'),
            description: t('Band performance with seamless character swap while preserving motion'),
        },
        {
            src: 'https://video.seedance2-ai.io/video/store/1607957509927/seedance-demo4-4-4m.mp4',
            label: t('Multi-Shot Extension'),
            description: t('Scene extended across multiple shots with consistent characters'),
        },
    ];
    return (<main className='relative overflow-hidden bg-[#0a0e17]'>
      <Seo templateTitle={t('Unrestricted Seedance 2.0 AI Video Generator | Text to Video, Image to Video by ByteDance')} description={t('Create stunning AI videos with Seedance 2.0 by ByteDance. Native audio-visual sync, 2K resolution, multi-shot storytelling, character consistency, and cinematic camera techniques. Generate videos from text, images, video references, and audio.')} image='https://easy-peasy.ai/api/og-template?title=Seedance%202.0%20AI%20Video%20Generator'/>

      {/* ================================================================= */}
      {/* HERO                                                              */}
      {/* ================================================================= */}
      <div className='relative min-h-[700px] sm:min-h-[90vh] max-h-[960px] w-full overflow-hidden'>
        {/* Background layers */}
        <div className='absolute inset-0'>
          {/* Noise grain */}
          <div className='absolute inset-0 opacity-[0.035]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }}/>
          {/* Radial glow center */}
          <div className='absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2' style={{
            width: '900px',
            height: '600px',
            background: 'radial-gradient(ellipse, rgba(20,184,166,0.12) 0%, rgba(6,182,212,0.06) 40%, transparent 70%)',
        }}/>
          {/* Edge accents */}
          <div className='absolute -top-32 -left-32 w-96 h-96 rounded-full bg-cyan-500/[0.07] blur-[100px]'/>
          <div className='absolute bottom-0 right-0 w-[500px] h-[400px] bg-teal-500/[0.05] blur-[120px]'/>
          {/* Film-strip line accents */}
          <div className='absolute top-0 left-[8%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
          <div className='absolute top-0 right-[8%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
        </div>

        {/* Navigation */}
        <div className='absolute top-0 left-0 w-full z-20'>
          <LandingPageNavigation variant='light'/>
        </div>

        {/* Hero content */}
        <div className='relative z-10 mx-auto max-w-7xl px-6 pt-36 sm:pt-52 lg:px-8 lg:pt-40 h-full flex flex-col justify-center'>
          <div className='mx-auto max-w-4xl text-center'>
            {/* Badge */}
            <div className='mb-10 flex justify-center'>
              <div className='inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 text-[13px] text-gray-400 backdrop-blur-sm'>
                <span className='relative flex h-1.5 w-1.5'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75'/>
                  <span className='relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-400'/>
                </span>
                {t("ByteDance's Next-Gen Video Model")}
              </div>
            </div>

            {/* Title */}
            <h1 className='text-5xl font-extrabold tracking-tight text-white sm:text-8xl leading-[0.9]'>
              <span className='block text-2xl sm:text-4xl font-bold tracking-[0.15em] uppercase mb-3' style={{
            background: 'linear-gradient(135deg, #5eead4 0%, #22d3ee 50%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                {t('Unrestricted')}
              </span>
              <span className='block'>Seedance</span>
              <span className='block mt-1' style={{
            background: 'linear-gradient(135deg, #5eead4 0%, #22d3ee 50%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                2.0
              </span>
            </h1>

            <p className='mt-8 text-lg leading-relaxed text-gray-400 max-w-2xl mx-auto sm:text-xl'>
              {t('Four input modalities. Native audio-visual sync. Cinematic camera reproduction. Multi-shot storytelling with extreme character consistency — all at up to 2K resolution.')}
            </p>

            {/* Availability badge */}
            <div className='mt-6 inline-flex items-center gap-2 rounded-full border border-teal-400/20 bg-teal-400/[0.06] px-5 py-2.5'>
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75'/>
                <span className='relative inline-flex h-2 w-2 rounded-full bg-teal-400'/>
              </span>
              <span className='text-sm font-semibold text-teal-300'>
                {t('Available Now')}
              </span>
            </div>

            {/* CTA */}
            <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link href='/ai-videos' className='group relative inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-[#0a0e17] transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #5eead4 0%, #22d3ee 100%)',
        }}>
                {t('Start Creating with Seedance 2.0')}
                <span className='ml-2 transition-transform group-hover:translate-x-0.5'>
                  →
                </span>
              </Link>
              <a href='#creation-modes' className='text-sm font-medium text-gray-400 hover:text-white transition-colors'>
                {t('Explore capabilities')}
              </a>
            </div>

            <div className='mt-8 flex justify-center'>
              <HappyUsers fromLandingPage={true} className='items-center'/>
            </div>

            {/* Stats row */}
            <div className='mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto'>
              {[
            { value: '2K', label: t('Resolution') },
            { value: '12', label: t('Input Files') },
            { value: '8+', label: t('Lip-Sync Languages') },
            { value: '30%', label: t('Faster') },
        ].map((s) => (<div key={s.label} className='text-center rounded-xl border border-white/[0.06] bg-white/[0.02] py-4 px-3'>
                  <div className='text-2xl font-bold sm:text-3xl' style={{ color: '#5eead4' }}>
                    {s.value}
                  </div>
                  <div className='mt-1 text-xs text-gray-500 uppercase tracking-wider'>
                    {s.label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className='absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0a0e17] to-transparent'/>
      </div>

      {/* Logos */}
      <LogosCloud />

      {/* ================================================================= */}
      {/* UNRESTRICTED — NO FACE BLOCKING                                    */}
      {/* ================================================================= */}
      <section className='py-16 sm:py-20 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-4xl px-6 lg:px-8 text-center'>
          <SectionLabel>{t('Unrestricted')}</SectionLabel>
          <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
            {t('No Face Blocking. Real People Allowed.')}
          </h2>
          <p className='text-lg text-gray-400 leading-relaxed'>
            {t('Most providers refuse any reference photo or clip that shows a recognisable real person — which rules out selfies, team shots, and real footage. Easy-Peasy.AI runs every Seedance job across two providers: when the first declines your upload for containing a person, we automatically resubmit it to one that accepts real people. Same prompt, same settings, same price.')}
          </p>
          <p className='mt-6'>
            <Link href='/ai-video-generator/seedance-2-5' className='text-sm font-medium text-teal-300 underline decoration-teal-300/30 underline-offset-4 hover:text-teal-200 transition-colors'>
              {t('See how the unrestricted pipeline works →')}
            </Link>
          </p>
        </div>
      </section>

      {/* ================================================================= */}
      {/* VIDEO EXAMPLES                                                    */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Video Examples')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('See Seedance 2.0 in Action')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Real outputs generated by Seedance 2.0 — from cinematic one-take tracking shots to audio-synced narratives and multi-character scenes.')}
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5'>
            {videoExamples.map((video) => (<div key={video.src} className='group relative'>
                <div className='relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]'>
                  <video src={video.src} muted loop playsInline preload='metadata' className='w-full aspect-video object-cover' onMouseEnter={(e) => (e.target as HTMLVideoElement).play().catch(() => { })} onMouseLeave={(e) => {
                const v = e.target as HTMLVideoElement;
                v.pause();
                v.currentTime = 0;
            }}/>
                  {/* Play hint overlay */}
                  <div className='absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none'>
                    <div className='h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center'>
                      <svg className='h-5 w-5 text-white ml-0.5' fill='currentColor' viewBox='0 0 24 24'>
                        <path d='M8 5v14l11-7z'/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className='mt-3 px-1'>
                  <h3 className='text-sm font-semibold text-white'>
                    {video.label}
                  </h3>
                  <p className='mt-1 text-xs text-gray-500'>
                    {video.description}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* CREATION MODES                                                    */}
      {/* ================================================================= */}
      <section id='creation-modes' className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Creation Modes')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Four Ways to Create')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Seedance 2.0 accepts text, images, video references, and audio as inputs — individually or combined for maximum creative control.')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {creationModes.map((mode) => (<div key={mode.tag} className='group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-all hover:border-teal-500/20 hover:bg-white/[0.04]'>
                <div className='flex items-start gap-4 mb-5'>
                  <span className='inline-flex items-center justify-center rounded-lg border border-teal-500/30 bg-teal-500/10 px-3 py-1.5 text-xs font-bold tracking-wider text-teal-300'>
                    {mode.tag}
                  </span>
                  <h3 className='text-xl font-bold text-white'>{mode.mode}</h3>
                </div>
                <p className='text-gray-400 leading-relaxed mb-6'>
                  {mode.description}
                </p>
                <ul className='space-y-2.5'>
                  {mode.details.map((d) => (<li key={d} className='flex items-start gap-2.5 text-sm text-gray-400'>
                      <CheckIcon />
                      <span>{d}</span>
                    </li>))}
                </ul>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* MULTIMODAL INPUT SYSTEM                                           */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            {/* Left — explanation */}
            <div>
              <SectionLabel>{t('Multimodal Input')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('Up to 12 Files. One Generation.')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Previously, getting complex camera movements or character-consistent multi-scene videos required writing elaborate prompts. With Seedance 2.0, you simply upload reference files and the model intelligently extracts what it needs.')}
              </p>
              <div className='space-y-4'>
                {[
            {
                count: t('Up to 9 images'),
                role: t('Characters, locations, props, style references'),
            },
            {
                count: t('Up to 3 videos'),
                role: t('Camera movements, motion patterns, expressions (15s max)'),
            },
            {
                count: t('Up to 3 audio files'),
                role: t('Voiceover, music, sound effects for sync (15s max)'),
            },
        ].map((item) => (<div key={item.count} className='flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
                    <span className='text-sm font-bold text-teal-300 whitespace-nowrap min-w-[110px]'>
                      {item.count}
                    </span>
                    <span className='text-sm text-gray-400'>{item.role}</span>
                  </div>))}
              </div>
            </div>

            {/* Right — visual diagram */}
            <div className='relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('Input Flow Diagram')}
              </div>

              {/* Input slots */}
              <div className='space-y-3 mb-8'>
                {[
            {
                label: 'Text Prompt',
                color: '#5eead4',
                width: '100%',
            },
            {
                label: '@Image1 — Character',
                color: '#22d3ee',
                width: '75%',
            },
            {
                label: '@Image2 — Location',
                color: '#22d3ee',
                width: '70%',
            },
            {
                label: '@Image3 — Style ref',
                color: '#22d3ee',
                width: '60%',
            },
            {
                label: '@Video1 — Camera',
                color: '#a78bfa',
                width: '80%',
            },
            {
                label: '@Audio1 — Voiceover',
                color: '#f472b6',
                width: '65%',
            },
        ].map((slot) => (<div key={slot.label} className='flex items-center gap-3'>
                    <span className='text-xs text-gray-500 w-40 text-right whitespace-nowrap hidden sm:block'>
                      {slot.label}
                    </span>
                    <span className='text-xs text-gray-500 w-28 text-right whitespace-nowrap sm:hidden'>
                      {slot.label.split(' — ')[0]}
                    </span>
                    <div className='flex-1 h-2.5 rounded-full bg-white/[0.04] overflow-hidden'>
                      <div className='h-full rounded-full transition-all' style={{
                width: slot.width,
                background: `linear-gradient(90deg, ${slot.color}40, ${slot.color}90)`,
            }}/>
                    </div>
                  </div>))}
              </div>

              {/* Arrow */}
              <div className='flex items-center justify-center my-6'>
                <div className='w-px h-8 bg-gradient-to-b from-teal-500/40 to-teal-500/10'/>
              </div>

              {/* Output */}
              <div className='rounded-xl border border-teal-500/20 bg-teal-500/[0.06] p-4 text-center'>
                <div className='text-sm font-bold text-teal-300'>
                  {t('Video + Synchronized Audio')}
                </div>
                <div className='text-xs text-gray-500 mt-1'>
                  {t('Up to 2K · 4–12s · Dialogue + Foley + Ambience')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* CAMERA TECHNIQUES                                                 */}
      {/* ================================================================= */}
      <section id='features' className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Cinematic Camera Control')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Hollywood Camera Techniques')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Describe camera movements in your prompt or upload a reference video — Seedance 2.0 extracts and reproduces professional cinematography techniques automatically.')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
            {cameraFeatures.map((cam) => (<div key={cam.name} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-cyan-500/20 hover:bg-white/[0.04]'>
                <h3 className='text-lg font-bold text-white mb-3'>
                  {cam.name}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed'>
                  {cam.description}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* AUDIO-VISUAL SYNC — HIGHLIGHT BAND                               */}
      {/* ================================================================= */}
      <section className='relative py-24 sm:py-32 overflow-hidden' style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #0c1929 50%, #0d1117 100%)',
        }}>
        {/* Decorative glow */}
        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none' style={{
            width: '700px',
            height: '400px',
            background: 'radial-gradient(ellipse, rgba(20,184,166,0.08) 0%, transparent 60%)',
        }}/>

        <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            <div>
              <SectionLabel>{t('Breakthrough')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('Native Audio-Visual Synchronization')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Unlike other models that add audio as a post-processing step, Seedance 2.0 generates high-fidelity audio as part of the core generation pipeline. Three intelligent audio layers are synchronized with visual content at the frame level.')}
              </p>
              <ul className='space-y-4 mb-10'>
                {[
            t('Dialogue — Phoneme-level lip-sync in 8+ languages'),
            t('Foley — Action-matched sound effects (footsteps, impacts, doors)'),
            t('Ambience — Environmental audio (wind, crowds, rain, traffic)'),
            t('Beat matching — Audio-visual rhythm synchronization for music'),
        ].map((item, i) => (<li key={i} className='flex items-start gap-3 text-gray-300'>
                    <CheckIcon />
                    <span className='text-sm'>{item}</span>
                  </li>))}
              </ul>
              <ButtonLink href='/ai-videos' variant='light' className='rounded-xl bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-base font-semibold shadow-lg'>
                {t('Try It Now')}
              </ButtonLink>
            </div>

            {/* Audio layers visualization */}
            <div className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('Audio Layer Stack')}
              </div>
              <div className='space-y-5'>
                {[
            {
                layer: t('Dialogue'),
                desc: t('Lip-synced speech · 8+ languages · Phoneme-level accuracy'),
                gradient: 'from-teal-400 to-cyan-400',
                pct: 90,
            },
            {
                layer: t('Foley'),
                desc: t('Footsteps · Impacts · Object interactions · Environmental SFX'),
                gradient: 'from-cyan-400 to-blue-400',
                pct: 82,
            },
            {
                layer: t('Ambience'),
                desc: t('Background atmosphere · Weather · Crowds · Spatial audio'),
                gradient: 'from-violet-400 to-purple-400',
                pct: 75,
            },
        ].map((item) => (<div key={item.layer}>
                    <div className='flex items-center justify-between mb-2'>
                      <span className='text-sm font-bold text-white'>
                        {item.layer}
                      </span>
                      <span className='text-xs text-gray-500'>
                        {item.pct}% {t('accuracy')}
                      </span>
                    </div>
                    <div className='w-full h-2 rounded-full bg-white/[0.06] overflow-hidden mb-2'>
                      <div className={`h-full rounded-full bg-gradient-to-r ${item.gradient}`} style={{ width: `${item.pct}%` }}/>
                    </div>
                    <p className='text-xs text-gray-500'>{item.desc}</p>
                  </div>))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* HOW IT WORKS                                                      */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Workflow')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('How It Works')}
            </h2>
          </div>

          <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {howItWorks.map((step) => (<div key={step.step} className='relative'>
                <div className='text-5xl font-black mb-4' style={{ color: 'rgba(94,234,212,0.15)' }}>
                  {step.step}
                </div>
                <h3 className='text-lg font-bold text-white mb-3'>
                  {step.title}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed'>
                  {step.description}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* PROMPT WRITING GUIDE                                              */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Prompt Guide')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Tips for Better Results')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Get the most out of Seedance 2.0 with these prompting techniques from power users and the official documentation.')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {promptTips.map((tip) => (<div key={tip.title} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
                <h3 className='text-lg font-bold text-white mb-3'>
                  {tip.title}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed mb-5'>
                  {tip.description}
                </p>
                <div className='rounded-xl bg-white/[0.03] border border-white/[0.06] p-4'>
                  <div className='text-[10px] font-bold tracking-wider uppercase text-gray-600 mb-2'>
                    {t('Example Prompt')}
                  </div>
                  <p className='text-sm text-gray-300 leading-relaxed italic'>
                    &ldquo;{tip.example}&rdquo;
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* VISUAL STYLES                                                     */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Visual Styles')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl'>
              {t('Any Aesthetic You Can Imagine')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Specify the visual style in your prompt or let the model infer it from reference images.')}
            </p>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
            {visualStyles.map((style) => (<div key={style.name} className='group rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center transition-all hover:border-teal-500/20 hover:bg-white/[0.04]'>
                <div className='text-base font-bold text-white'>
                  {style.name}
                </div>
                <div className='text-xs text-gray-500 mt-1'>{style.desc}</div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* TECHNICAL SPECS                                                   */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Specifications')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Technical Details')}
            </h2>
          </div>

          <div className='mx-auto max-w-5xl grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
            {specs.map((spec) => (<div key={spec.label} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5'>
                <div className='text-xs text-gray-500 uppercase tracking-wider'>
                  {spec.label}
                </div>
                <div className='text-2xl font-bold mt-2' style={{ color: '#5eead4' }}>
                  {spec.value}
                </div>
                <div className='text-xs text-gray-500 mt-1'>{spec.sub}</div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* VERSION COMPARISON                                                */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Comparison')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('2.0 vs 1.5 Pro')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Every dimension improved — resolution, audio, inputs, speed, and creative control.')}
            </p>
          </div>

          <div className='mx-auto max-w-4xl overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='border-b border-white/[0.08]'>
                  <th className='py-4 px-4 text-left text-sm font-medium text-gray-500'>
                    {t('Feature')}
                  </th>
                  <th className='py-4 px-4 text-left text-sm font-bold text-teal-300'>
                    {t('Seedance 2.0')}
                  </th>
                  <th className='py-4 px-4 text-left text-sm font-medium text-gray-600'>
                    {t('Seedance 1.5 Pro')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((item, i) => (<tr key={item.feature} className={i % 2 === 0 ? 'bg-white/[0.01]' : 'bg-transparent'}>
                    <td className='py-3.5 px-4 text-sm font-medium text-gray-300'>
                      {item.feature}
                    </td>
                    <td className='py-3.5 px-4 text-sm text-gray-200'>
                      <span className='inline-flex items-center gap-2'>
                        <CheckIcon />
                        {item.v2}
                      </span>
                    </td>
                    <td className='py-3.5 px-4 text-sm text-gray-600'>
                      {item.v15}
                    </td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* USE CASES                                                         */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Use Cases')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('What You Can Create')}
            </h2>
          </div>

          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
            {useCases.map((uc) => (<div key={uc.title} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-teal-500/20 hover:bg-white/[0.04]'>
                <span className='text-2xl'>{uc.icon}</span>
                <h3 className='mt-3 text-base font-bold text-white'>
                  {uc.title}
                </h3>
                <p className='mt-2 text-sm text-gray-400 leading-relaxed'>
                  {uc.description}
                </p>
              </div>))}
          </div>

          <div className='mt-16 flex items-center justify-center'>
            <ButtonLink href={HAS_FREE_PLAN ? '/ai-videos' : '/#pricing'} variant='dark' className='px-8 py-3 text-lg'>
              {t('Start Creating with Seedance 2.0')}
            </ButtonLink>
          </div>
        </div>
      </section>

      {/* Shared sections on light background */}
      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      {/* ================================================================= */}
      {/* FAQ                                                               */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('FAQ')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Frequently Asked Questions')}
            </h2>
            <p className='mt-4 text-lg text-gray-400'>
              {t('Everything you need to know about Seedance 2.0')}
            </p>
          </div>

          <div className='mx-auto max-w-3xl space-y-4'>
            {faqs.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.06] bg-white/[0.02]'>
                <summary className='flex cursor-pointer items-center justify-between p-5 text-base font-semibold text-white'>
                  {faq.question}
                  <svg className='h-5 w-5 text-gray-600 transition-transform group-open:rotate-180 flex-shrink-0 ml-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                  </svg>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm text-gray-400 leading-relaxed'>
                    {faq.answer}
                  </p>
                </div>
              </details>))}
          </div>
        </div>
      </section>

      {/* CTA & Footer on default bg */}
      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos'/>
        <Footer />
      </div>
    </main>);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a12e86ed8b__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/seedance-2',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ed8b__ as getStaticProps }
  